<template>
  <div id="importData">
    <div class="drawer-dimmer" v-if="importingData"></div>
    <p class="mb-1"><strong>Import Data to Existing Datapoints</strong><br>You may upload data from a CSV. We recommend downloading a CSV from your dataset under the 'Downloads' tab (<i class="fas fa-cloud-download-alt"></i>) to use as a template. Imported data will replace current data and cannot be undone. Use with caution! CSV headers cannot contain any of the following characters: '~', '*', '/', '[', or ']'.</p>
    <input
      type="file"
      id="importFile"
      name="importFile"
      value="Import"
      class="mb-2 mt-2"
      accept=".csv"
      @change="selectFile"
    />

    <div
      v-if="machineNames.length > 0"
      class="mt-3"
    >
      <p class="mb-1"><strong>Headers to Sync</strong></p>
      <div
        v-for="(machineName, i) in machineNames"
        v-bind:key="i"
        class="header-pill"
      >
        <b-badge
          v-if="machineName !== 'dataPointId' && machineName !== 'updatedOn' && machineName !== 'createdOn'"
          pill
          variant="primary"
          class="mr-1"
        >{{ machineName }}</b-badge>
      </div><!-- /v-for -->
    </div><!-- /v-if -->

    <div
      class="list-group mt-3"
      v-if="proposedHeaders.length > 0"
    >
      <p class="mb-1"><strong>New Headers</strong></p>
      <div
        class="list-container"
        v-for="(proposedHeader, index) in proposedHeaders"
        v-bind:key="index"
      >
        <div class="list-group-item mb-2">
          {{ proposedHeader.name }} ({{ proposedHeader.type }})
        </div><!-- /.list-group-item -->
      </div><!-- /.list-container -->
    </div><!-- /.list-group -->

    <div v-if="readyToImport" class="mt-3">
      <b-btn
        v-if="!importingData"
        @click="confirmImportData = true"
        class="btn-primary w-100"
      >
        <i class="fas fa-file-import"></i> Import
      </b-btn>
      <b-btn
        v-else
        class="btn-secondary w-100"
      >
         <i class="fas fa-sync fa-spin"></i> Importing...
      </b-btn>
    </div><!-- /v-if readyToImport -->
    <Confirm
      v-if="confirmImportData"
      text="Are you sure you'd like to import this data? Remember that this will override any existing data and cannot be undone!"
      v-on:confirmed="importData($event)"
    />
  </div><!-- /#importData -->
</template>

<script>
import Confirm from '../Confirm'
import Papa from 'papaparse'
import firebase from 'firebase/compat/app'
const fb = require('../../firebaseConfig.js')

export default {
  data () {
    return {
      confirmImportData: false,
      importingData: false,
      proposedHeaders: [],
      readyToImport: false,
      jsonData: [],
      headers: [],
      machineNames: [],
      headerTypes: []
    }
  },
  components: {
    Confirm
  },
  computed: {
    collector: {
      get () {
        return this.$store.state.collector
      },
      set (value) {
        this.$store.commit('setCollector', value)
      }
    }
  },
  methods: {
    updateDataset () {
      // Create compilation request and wait for completion
      fb.compileRequestsCollection.add({
        createdBy: this.$store.state.currentUser.uid,
        createdOn: new Date(),
        collectorId: this.$store.state.dataset.collectorId,
        datasetId: this.$store.state.dataset.id,
        lastUpdated: this.$store.state.dataset.updatedOn,
        farmId: this.$store.state.farm.id,
        complete: false
      }).then(compileRequest => {
        // Wait for compilation to complete
        const unsubscribe = compileRequest.onSnapshot(docSnapshot => {
          const data = docSnapshot.data()
          if (data.complete) {
            unsubscribe()
            // Get the updated dataset doc
            fb.datasetsCollection.doc(this.$store.state.dataset.id).get().then(datasetDoc => {
              const newDataset = datasetDoc.data()
              newDataset.id = datasetDoc.id
              // Set new dataset
              this.$store.commit('setStateProperty', { property: 'dataset', value: newDataset })
              // Relaunch dataset with updated dataset
              this.$store.commit('hideDrawer', 'datasetDetails')
              this.$store.dispatch('removeAllLayers')
              setTimeout(() => {
                this.$store.dispatch('launchDataset', { dataset: newDataset })
              }, 300)
            })
          }
        })
      })
    },
    selectFile (e) {
      this.headers = []
      this.machineNames = []
      this.proposedHeaders = []
      this.headerTypes = []
      this.readyToImport = false
      this.jsonData = []
      const fileName = e.target.files[0].name
      const lastDot = fileName.lastIndexOf('.')
      const fileExt = fileName.substring(lastDot + 1)
      const reader = new FileReader()
      if (fileExt === 'csv') {
        reader.onload = this.onCSVLoad
        reader.readAsText(event.target.files[0])
      }
    },
    onCSVLoad (e) {
      const obj = Papa.parse(e.target.result, { header: false })
      this.jsonData = obj.data
      this.headers = obj.data[0]
      this.headers.forEach((header, i) => {
        const regex = /[&\/\\#,+()$~%.'":*?<>{}]/g // eslint-disable-line
        const testRegex = regex.test(header)
        if (testRegex) {
          header = header.replace(regex, '_')
        }
        const standardHeaders = [
          'dataPointId',
          'updatedOn',
          'updatedBy',
          'createdOn',
          'createdBy',
          'geolocation',
          'accuracy'
        ]
        const coordinateHeaders = [
          'longitude',
          'latitude',
          'lng',
          'lat'
        ]
        
        // Coordinate headers are handled separately for geometry updates, but
        // they still hold their place: machineNames/headerTypes are indexed by
        // column position, and skipping without a placeholder shifted every
        // column after longitude/latitude (a new column at the far right of the
        // downloaded CSV was silently ignored).
        if (coordinateHeaders.includes(header.toLowerCase())) {
          this.machineNames.push(null)
          this.headerTypes.push(null)
          return
        }
        
        if (standardHeaders.includes(header)) {
          // Check for standard headers
          this.machineNames.push(header)
          this.headerTypes.push('string')
        } else {
          // Check for defined headers if it is a new header, add to proposed headers
          let newProposedHeader = true
          if (this.$store.state.collector.fields) {
            this.$store.state.collector.fields.forEach(collectorField => {
              if (collectorField.label === header) {
                this.machineNames.push(collectorField.machine_name)
                this.headerTypes.push(collectorField.type)
                newProposedHeader = false
              }
            })
          }
          if (newProposedHeader) {
            let headerType = 'Number'
            if (isNaN(this.jsonData[1][i])) {
              headerType = 'Text'
            }
            const proposedHeader = {
              name: header,
              type: headerType
            }
            this.proposedHeaders.push(proposedHeader)
            this.machineNames.push(header)
            this.headerTypes.push(headerType)
          }
        }
      })
      console.log(this.proposedHeaders)
      console.log(this.machineNames)
      console.log(this.headerTypes)
      if (this.jsonData.length > 0) {
        this.readyToImport = true
      }
    },
    importData (e) {
      // Rows that failed to write (or couldn't be matched) — reported in the summary.
      const failures = []
      async function updateDatapoint (id, obj, hasChanges, longitude, latitude) {
        console.log(id)
        console.log(obj)
        console.log('Has changes:', hasChanges)
        console.log('Coordinates:', { longitude, latitude })
        if (id && hasChanges) {
          // Add geometry update if coordinates are provided
          if (longitude !== null && latitude !== null) {
            obj.geolocation = new firebase.firestore.GeoPoint(latitude, longitude)
          }
          
          // Get current datapoint document to create revision
          const datapointDoc = await fb.collectorsCollection.doc(this.$store.state.dataset.collectorId).collection('dataPoints').doc(id).get()
          if (datapointDoc.exists) {
            const datapointData = datapointDoc.data()
            
            // Create a clean copy for revision (without id field to avoid conflicts)
            const revisionData = { ...datapointData }
            delete revisionData.id

            // Create a revision in the 'revisions' subcollection
            await fb.collectorsCollection.doc(this.$store.state.dataset.collectorId).collection('dataPoints').doc(id).collection('revisions').add(revisionData)

            // Add revision message to update data
            obj.revisionMessage = 'Updated as part of CSV import.'
          }
          
          await fb.collectorsCollection.doc(this.$store.state.dataset.collectorId).collection('dataPoints').doc(id).update(obj).then(() => {
            return true
          }).catch(err => {
            // Previously swallowed: the import reported success while the
            // row never wrote. Surface it.
            console.log(err)
            failures.push(id + ': ' + ((err && err.message) || err))
            return false
          })
        } else if (id && !hasChanges) {
          console.log('No changes detected for datapoint:', id)
          return true
        } else {
          if (obj && Object.keys(obj).length > 2) failures.push('a row with no dataPointId')
          return false
        }
      }
      
      this.confirmImportData = false
      if (e) {
        // Rows are matched to existing datapoints by the dataPointId column
        // the CSV download includes. Without it nothing can be matched —
        // previously every row was silently "skipped" while any NEW columns
        // were still added to the collector, so a grower saw the header
        // appear with no data in it and no explanation.
        if (!this.headers.includes('dataPointId')) {
          this.$store.commit('setAlert', {
            active: true,
            message: 'This CSV has no "dataPointId" column, so its rows can\'t be matched to your datapoints. Download the CSV from this dataset first (Downloads tab), add your columns to that file, and import it.',
            level: 'alert-danger',
            timeout: 15000
          })
          return
        }
        const updatePromises = []
        this.importingData = true
        let updatedCount = 0
        let skippedCount = 0
        
        this.jsonData.forEach((datapoint, i) => {
          // Blank lines (the trailing newline Excel writes, or empty rows) are
          // not data: skip them instead of reporting "a row with no dataPointId".
          if (!Array.isArray(datapoint) || datapoint.every(cell => cell === null || cell === undefined || String(cell).trim() === '')) return
          // Skip first header row
          if (i > 0) {
            const datapointObject = {
              updatedOn: new Date(),
              updatedBy: this.$store.state.currentUser.uid
            }
            let dataPointIdVal = false
            let hasChanges = false
            let longitude = null
            let latitude = null
            console.log(datapoint)
            
            datapoint.forEach((datapointField, me) => {
              const restrictedTerms = [
                'createdBy',
                'createdOn',
                'updatedBy',
                'updatedOn',
                'dataPointId',
                'geolocation',
                'accuracy'
              ]
              
              // Handle coordinate fields separately
              const headerName = this.headers[me].toLowerCase()
              if (headerName === 'longitude' || headerName === 'lng') {
                longitude = parseFloat(datapointField)
              } else if (headerName === 'latitude' || headerName === 'lat') {
                latitude = parseFloat(datapointField)
              } else if (!this.machineNames[me]) {
                // No mapping for this column (blank header): ignore it.
              } else if (!restrictedTerms.includes(this.machineNames[me])) {
                let fieldValue
                if (this.headerTypes[me] === 'Number') {
                  fieldValue = this.parseNumberCell(datapointField)
                } else {
                  fieldValue = datapointField
                }
                datapointObject[this.machineNames[me]] = fieldValue
              } else if (this.machineNames[me] === 'dataPointId') {
                dataPointIdVal = datapointField
              }
            })
            
            // Check for changes by comparing with existing datapoint
            const prom = this.checkForChanges(dataPointIdVal, datapointObject, longitude, latitude).then(changes => {
              hasChanges = changes
              if (hasChanges) {
                updatedCount++
              } else {
                skippedCount++
              }
              return updateDatapoint.call(this, dataPointIdVal, datapointObject, hasChanges, longitude, latitude)
            })
            updatePromises.push(prom)
          }
        })
        
        Promise.all(updatePromises).then(() => {
          console.log('Promises complete')
          this.readyToImport = false
          this.importingData = false
          
          let message = failures.length ? 'Import finished with problems. ' : 'Import complete! '
          if (updatedCount > 0) {
            message += `${updatedCount} datapoints updated. `
          }
          if (skippedCount > 0) {
            message += `${skippedCount} datapoints skipped (no changes). `
          }
          if (failures.length) {
            message += `${failures.length} rows could not be written (first: ${failures[0]}).`
          }
          this.$store.commit('setAlert', {
            active: true,
            message: message,
            level: failures.length ? 'alert-warning' : 'alert-success',
            timeout: failures.length ? 15000 : 5000
          })

          if (this.proposedHeaders.length > 0) {
            if (!this.collector.fields) {
              this.collector.fields = []
            }
            console.log('Adding new fields')
            this.proposedHeaders.forEach((proposed, i) => {
              this.collector.fields.push({
                label: proposed.name,
                type: proposed.type,
                machine_name: proposed.name,
                min: false,
                max: false,
                options: [],
                step: false,
                suffix: false,
                required: false
              })
            })
          }
          return fb.collectorsCollection.doc(this.$store.state.dataset.collectorId).update({
            fields: this.collector.fields || [],
            reCompile: true
          })
        }).then(() => {
          // Keep the dataset doc's headers in step with the collector's
          // fields — for collector datasets the viz pickers read stored
          // headers, so a new column stayed invisible there until the
          // collector was re-saved by hand.
          const labels = (this.collector.fields || []).map(f => f.label).filter(Boolean)
          if (labels.length) {
            return fb.datasetsCollection.doc(this.$store.state.dataset.id).update({ headers: labels }).catch(() => {})
          }
        }).then(() => {
          this.updateDataset()
        }).catch(err => {
          console.log(err)
          this.$store.commit('setAlert', { active: true, message: 'Errors importing data!', level: 'alert-danger', timeout: 5000 })
        })
      }
    },
    // Number cells straight from spreadsheets: blank → null (not NaN);
    // "12,5" (French/European locale) → 12.5; "1,234" and "1,234.5" →
    // thousands separators (the platform's own exports use dot decimals, so
    // a comma followed by exactly three digits reads as a thousands group
    // unless the integer part is 0); "1.234,5" → 1234.5.
    parseNumberCell (raw) {
      if (raw === null || raw === undefined) return null
      const s = String(raw).trim()
      if (s === '') return null
      let normalized = s
      if (/^-?\d{1,3}(\.\d{3})+,\d+$/.test(s)) {
        normalized = s.replace(/\./g, '').replace(',', '.')
      } else if (/^-?\d+,\d+$/.test(s)) {
        const [int, frac] = s.split(',')
        const thousands = frac.length === 3 && int.replace('-', '') !== '0'
        normalized = thousands ? int + frac : int + '.' + frac
      } else if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) {
        normalized = s.replace(/,/g, '')
      }
      const n = parseFloat(normalized)
      return Number.isFinite(n) ? n : null
    },
    async checkForChanges (dataPointId, newData, longitude, latitude) {
      try {
        const datapointDoc = await fb.collectorsCollection.doc(this.$store.state.dataset.collectorId).collection('dataPoints').doc(dataPointId).get()
        
        if (!datapointDoc.exists) {
          console.log('Datapoint not found:', dataPointId)
          return false
        }
        
        const existingData = datapointDoc.data()
        
        // Compare each field in newData with existing data
        for (const [key, newValue] of Object.entries(newData)) {
          // Skip metadata fields that we always update
          if (['updatedOn', 'updatedBy'].includes(key)) {
            continue
          }
          
          const existingValue = existingData[key]
          
          // Handle different data types for comparison
          if (typeof newValue === 'number' && typeof existingValue === 'number') {
            if (Math.abs(newValue - existingValue) > 0.000001) { // Small tolerance for floating point
              console.log(`Change detected in ${key}: ${existingValue} -> ${newValue}`)
              return true
            }
          } else if (newValue !== existingValue) {
            console.log(`Change detected in ${key}: ${existingValue} -> ${newValue}`)
            return true
          }
        }
        
        // Handle geometry updates
        if (longitude !== null || latitude !== null) {
          const existingGeometry = existingData.geolocation
          if (existingGeometry) {
            const existingLat = existingGeometry.latitude || existingGeometry._lat
            const existingLng = existingGeometry.longitude || existingGeometry._lng
            
            // Only update if both coordinates are provided and they're different
            if (longitude !== null && latitude !== null) {
              if (Math.abs(existingLat - latitude) > 0.000001 || Math.abs(existingLng - longitude) > 0.000001) {
                console.log(`Geometry changed: (${existingLat}, ${existingLng}) -> (${latitude}, ${longitude})`)
                return true
              }
            }
          } else if (longitude !== null && latitude !== null) {
            // No existing geometry, but new coordinates provided
            console.log('Adding new geometry')
            return true
          }
        }
        
        console.log('No changes detected for datapoint:', dataPointId)
        return false
      } catch (error) {
        console.error('Error checking for changes:', error)
        return false
      }
    }
  }
}
</script>

<style scoped>
.header-pill {
  display: inline-block;
}
</style> 
